/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { css, useTheme } from "@emotion/react";
import highriskicon from "../../../../assets/highrisk.svg";
import mediumriskicon from "../../../../assets/mediumrisk.svg";
import unproductiveicon from "../../../../assets/Unproductive-risk.svg";
import flaggedicon from "../../../../assets/Flagged-risk.svg";
import link from '../../../../assets/link.svg';
import { useLanguage } from "LanguageContext";
import { useAnalysis } from "../FormComponents/AnalysisResultContext";
import { OnSortProps, THeaderProps } from "components/MainComponents/DataTable/THeader";
import DataTable from "../../DataTable/DataTable";
import RateFeedback from "components/CommonUi/RateFeedback/RateFeedback";
import Tooltip from "components/CommonUi/Tooltip/Tooltip";

interface ScrutinizedWordsProps {
    resultData: any;
}

export default function ScrutinizedWords({resultData}: ScrutinizedWordsProps) {
  const {analysisContextResult} = useAnalysis();
  const theme = useTheme();
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [sortState, setSortState] = useState<{
    key: string | null;
    direction: "asc" | "desc" | null;
  }>({ key: null, direction: null });

  const iconStyle = (iconBgColor?: any) => css`
    color: ${iconBgColor ? '' : theme.colors.white};
    width: 16px;
    height: 16px;
    margin-top: -2px;
  `;

  const scrutineMainWrapper =css`
    padding:30px;
    width: 100%;
    box-sizing: border-box;
    background-color: ${theme.colors.grey};
    border-radius: 8px;
    margin-top: 20px;
  `;

  const scrutineSectionWrapper = css`
    background-color: #fff;
    padding: 40px;
  `;
  const headerTextStyle = css`
    font-weight: 600;
    font-size: 30px;
    leading-trim: none;
    line-height: 120%;
    letter-spacing: 0%;
    margin-bottom: 20px;

    span{
    font-weight: 500;
    color: #3D5360;
    }

  `;

  const headerWrapper = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    width: 100%;
    border-bottom: 1.5px solid #EAEEF0;
  `;

  const tableClassStyles = css`
    min-width: auto;
    padding: 20px;
  `;

  const containerWrapperClassStyle = css`
    padding-top: 0px;
    > div{
    //  overflow: auto;
     padding: 4px;
     }
  `;

  const linkStyle = css`
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
      color: ${theme.colors.primary};
    `;
    const tooltipLinkMsgStyle = css`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    overflow: hidden;
    text-overflow: ellipsis;

    align-items: center;
    gap: 8px;
    font-weight: 500;
    line-height: 20px;
    color: ${theme.colors.primary};
  `;
    const linkIconStyle = css`
      width: 12px;
      height: 12px;
    `;
  
    const tagsStyles = css`
      display: flex;
      align-items: center;
      gap: 6px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      line-height: 150%;
      text-align: center;
      padding: 2px 8px;
      width: max-content;
    `;

    const labelText = css `
        font-weight: 600;
        font-size: 14px;
        line-height: 150%;
        text-align: center;
        color: #3D5360;
        span {
         font-weight: 600;
        }
      `;
  const highRiskbg = css`
      background-color: #FFEEEE;
    `;
      
    const highRisk = css`
      color: #B82223;    
    `;
  
    const mediumRiskbg = css`
      background-color: #FFF9CF;
    `;
  
    const mediumRisk = css`
      color: #AD5F00;
    `;
    const unproductiveRiskbg = css`
      background-color: #F1E2FF;
    `;
  
    const unproductiveRisk = css`
      color: #552E75;
    `;
  
    const lowRiskbg = css`
      background-color: #DEECF8;
    `;
  
    const lowRisk = css`
      color: #5894D9;
    `;

  const activeStyle = css`
      border-bottom: 2px solid #017E7E;
    `;
    
  const subSectionWrapper = css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 4px;
    padding: 10px 8px;
    cursor: pointer;
    transition: font-weight 0.3s ease;
    flex: 0 1 auto;
    border-bottom: 2px solid transparent;

    @media (max-width: 768px) {
      flex: 1 1 100%;
      max-width: 100%;
    }
  `;

  const paginationClassNameStyle = css`
    display: none;
  `;

  const polarizationConfig: Record<string, { style: any; bg: any; icon: string; label: string }> = {
    High: {
      style: tagsStyles,
      bg: highRiskbg,
      icon: highriskicon,
      label: t("risk_flagging_words_high", null, analysisContextResult?.locale_code),
    },
    Medium: {
      style: tagsStyles,
      bg: mediumRiskbg,
      icon: mediumriskicon,
      label: t("risk_flagging_words_medium", null, analysisContextResult?.locale_code),
    },
    Unproductive: {
      style: tagsStyles,
      bg: unproductiveRiskbg,
      icon: unproductiveicon,
      label: t("risk_flagging_words_unproductive", null, analysisContextResult?.locale_code),
    },
    Low: {
      style: tagsStyles,
      bg: lowRiskbg,
      icon: flaggedicon,
      label: t("risk_flagging_words_awareness", null, analysisContextResult?.locale_code),
    },
  };

  const isValidUrl = (value?: string): boolean => {
    if (typeof value !== "string") return false;
  
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return false;
  
    return trimmed.startsWith("http://") || trimmed.startsWith("https://");
  };  
  
  const tableColumns : THeaderProps[] = [
      {
        key: "word",
        width: "30%",
        title: t("risk_flagging_words_term", null, analysisContextResult?.locale_code),
        isSortable: true,
        sortDirection: null,
        onSort: ({ key, direction }: OnSortProps) => {
          setSortState({ key: direction ? key : null, direction: direction });
        },
      },
      {
        key: "polarization_level",
        width: "25%",
        title: t("risk_flagging_words_risk_level", null, analysisContextResult?.locale_code),
        isSortable: true,
        sortDirection: null,
        onSort: ({ key, direction }: OnSortProps) => {
          setSortState({ key: direction ? "levelInt" : null, direction: direction });
        },
        renderRowContent : (rowData: any) => {
          const config = polarizationConfig[rowData.polarization_level] || polarizationConfig.Low;
          return (
            <div css={[config.style, config.bg]}>
              <img src={config.icon} alt="alert" css={[linkIconStyle]} /> {config.label}
            </div>
          );
        },
      },
      {
        key: "number_of_occurences",
        width: "20%",
        title: t("risk_flagging_words_number_of_matches", null, analysisContextResult?.locale_code),
        isSortable: false,
        sortDirection: null,
        onSort: ({key, direction}: OnSortProps) => {},
      },
      {
        key: "link",
        width: "15%",
        title: t("risk_flagging_words_source", null, analysisContextResult?.locale_code),
        isSortable: false,
        sortDirection: null,
        onSort: ({key, direction}: OnSortProps) => {},
        renderRowContent: (rowData: any) =>
          isValidUrl(rowData?.link) ? (
            <div
              css={linkStyle}
              onClick={() =>
                window.open(
                  rowData.link,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              Link
              <img
                src={link}
                alt="alert"
                css={linkIconStyle}
              />
            </div>
          ) : rowData?.link?.length >= 20 ? (
            <Tooltip content={rowData?.link} usePortal>
              <div css={tooltipLinkMsgStyle}>
                {rowData?.link}
              </div>
            </Tooltip>
          ) : (
            <div css={tooltipLinkMsgStyle}>
              {rowData?.link}
            </div>
          )              
      },
      {
        key: "actions",
        width: "10%",
        isSortable: false,
        title: "",
        className: "",
        sortDirection: null,
        onSort: () => {},
      }
    ];

  const _totalWordList = [...(resultData?.risk_assessment?.[0]?.response || [])];

  const _validWordList = Array.isArray(_totalWordList) ? _totalWordList.filter((_item: any) => _item?.polarization_level?.trim() !== "") : [];

  type SegregatedResult<T> = {
    high: T[];
    medium: T[];
    low: T[];
    unproductive: T[];
    unspecified: T[];
  };

  function sortPolarizedValueImmutable(key: string, direction: "asc" | "desc", arr: any[]) {
    if (!key || !direction) return arr.slice();

    const copy = arr.slice();

    let forLower = (direction === "asc") ? 1 : -1
    let forHigher = (direction === "asc") ? -1 : 1

    copy.sort((a, b) => {
      if (a[key] < b[key]) return forHigher;
      if (a[key] > b[key]) return forLower;
      return 0;
    });
    return copy;
  }
    
  function segregateByPolarization<T extends { polarization_level?: string }>(
    data: T[] = []
  ): SegregatedResult<T> {
    const sortValue: any = {
      "high": 4,
      "medium": 2,
      "unproductive": 3,
      "low": 1,
      "unspecified": 0,
    }
    const _segregatedData: any = { high: [], medium: [], low: [], unproductive: [], unspecified: [] };
    data.forEach((item: any, index) => {
      let level= item.polarization_level?.trim();
      if (!level) level = "Unspecified";
      level = level?.toLocaleLowerCase()
      if (!_segregatedData[level]) {
        _segregatedData[level] = [];
      }
      if(!item?.index) item.index = index;
      if(!item?.number_of_occurences) item.number_of_occurences = item?.occurrences?.length || 1;
      _segregatedData[level].push({...item, levelInt: sortValue[level]});
    });
    return _segregatedData;
  }
    
  const segregatedData = segregateByPolarization(_validWordList);

  const groupedPlarisedList = [
  {
    id: "All",
    label: "All",
    count: "",
    icon: "",
    iconStyle: [iconStyle],
    labelStyle: labelText,
    infoDsc: "",
  },
  {
    id: "high",
    label: t("risk_flagging_words_high", null, analysisContextResult?.locale_code),
    count: segregatedData.high.length,
    icon: highriskicon,
    iconStyle: [iconStyle],
    labelStyle: labelText,
    infoDsc: t("risk_high_info", null, analysisContextResult?.locale_code),
  },
  {
    id: "medium",
    label: t("risk_flagging_words_medium", null, analysisContextResult?.locale_code),
    count: segregatedData.medium.length,
    icon: mediumriskicon,
    iconStyle: [iconStyle],
    labelStyle: labelText,
    infoDsc: t("risk_medium_info", null, analysisContextResult?.locale_code),
  },
  {
    id: "unproductive",
    label: t("risk_flagging_words_unproductive", null, analysisContextResult?.locale_code),
    count: segregatedData.unproductive?.length,
    icon: unproductiveicon,
    iconStyle: [iconStyle],
    labelStyle: labelText,
    infoDsc: t("risk_unproductive_info", null, analysisContextResult?.locale_code),
  },
  {
    id: "low",
    label: t("risk_flagging_words_awareness", null, analysisContextResult?.locale_code),
    count: segregatedData.low.length,
    icon: flaggedicon,
    iconStyle: [iconStyle],
    labelStyle: labelText,
    infoDsc: t("risk_awareness_info", null, analysisContextResult?.locale_code),
  }
  ];

  const _politicalRiskFeedbacks = analysisContextResult?.feedbacks.filter((item: any) => item.view === "Political Risk Assessment");

  const getFilteredList = () => {
    if (activeIndex === -1) return [...segregatedData.high, ...segregatedData.medium, ...segregatedData.unproductive, ...segregatedData.low];
    let polarizationSelected = groupedPlarisedList?.[activeIndex]?.id
    if (polarizationSelected && segregatedData[polarizationSelected as keyof SegregatedResult<any>]) {
      return segregatedData[polarizationSelected as keyof SegregatedResult<any>];
    }
    return [...segregatedData.high, ...segregatedData.medium, ...segregatedData.unproductive, ...segregatedData.low];
  };
  
  const [bckpFilteredWordList, setBckpFilteredWordList] = useState<any[]>([]);

  useEffect(() => {
    if (sortState.key && sortState.direction) {
      const sorted = sortPolarizedValueImmutable(sortState.key, sortState.direction, getFilteredList());
      setBckpFilteredWordList(sorted);
    } else {
      setBckpFilteredWordList(getFilteredList());
    }
    
  }, [activeIndex, sortState]);

  useEffect(() => {
    if (!bckpFilteredWordList?.length) {
      setBckpFilteredWordList(getFilteredList());
    }    
  }, [segregatedData])

  const handleTabClick = (index: number) => {
    setActiveIndex(prev => (prev === index ? -1 : index));
  };
  return (
    <div css={scrutineMainWrapper}>
        <p css={headerTextStyle}>{t("risk_flagging_words_header", null, analysisContextResult?.locale_code)} <span>{_validWordList?.length}</span></p>

        <div css={scrutineSectionWrapper}>
        <div css={headerWrapper}>
            {groupedPlarisedList.map((risk, index) => (
            <Tooltip key={risk.id} content={risk?.infoDsc}>
            <div
                css={[subSectionWrapper, index === activeIndex && activeStyle]}
                onClick={() => handleTabClick(index)}
            >
                {risk?.icon && <img src={risk.icon} alt="alert" css={risk.iconStyle} />}
                <div css={risk.labelStyle}>
                <span>{risk.count}</span> {risk.label}
                </div>
            </div>
            </Tooltip>
            ))}
        </div>
        <div>
            <DataTable
              tableData={bckpFilteredWordList}
              columns={tableColumns}
              isTableDataLoading={false}
              paginationClassName={css(paginationClassNameStyle)}
              isRowAccordion={true}
              tableClassName={tableClassStyles}
              no_data_found_msg={t("risk_flagging_words_no_data",null, analysisContextResult?.locale_code)}
            />
        </div>
        {<RateFeedback title={t("feedback_buttonText", null, analysisContextResult?.locale_code)} feedbackPosition="Political Risk Assessment" id="" entityId={analysisContextResult?.id} entityType="analysis_id" feedbackDetails={_politicalRiskFeedbacks?.[0]}/>}
        </div>
    </div>
  );
}
